import React from 'react';
import { 
  Folder, FolderOpen, Archive, Package, Heart, Star, Bookmark,
  Image, Camera, FileText, Video, Music, Tag, Tags, Book, BookOpen,
  Library, Briefcase, Clipboard, Settings, Wrench, Palette,
  Brush, Globe, Map, Clock, Calendar, Shield, Lock, Zap, Target,
  Trophy, Coffee, Home, Car, Plane, Gamepad2, ShoppingBag, Utensils,
  GraduationCap, Stethoscope, Building, Factory, Users, User,
  type LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

export type CategoryIcon = 
  | 'folder' | 'folder-open' | 'archive' | 'package' | 'heart' | 'star' | 'bookmark'
  | 'image' | 'camera' | 'file-text' | 'video' | 'music' | 'tag' | 'tags' | 'book' 
  | 'book-open' | 'library' | 'briefcase' | 'clipboard' | 'settings' | 'wrench' 
  | 'palette' | 'brush' | 'globe' | 'map' | 'clock' | 'calendar' 
  | 'shield' | 'lock' | 'zap' | 'target' | 'trophy' | 'coffee' | 'home' | 'car' 
  | 'plane' | 'gamepad' | 'shopping-bag' | 'utensils' | 'graduation-cap' 
  | 'stethoscope' | 'building' | 'factory' | 'users' | 'user';

const CATEGORY_ICONS: Record<CategoryIcon, LucideIcon> = {
  'folder': Folder,
  'folder-open': FolderOpen,
  'archive': Archive,
  'package': Package,
  'heart': Heart,
  'star': Star,
  'bookmark': Bookmark,
  'image': Image,
  'camera': Camera,
  'file-text': FileText,
  'video': Video,
  'music': Music,
  'tag': Tag,
  'tags': Tags,
  'book': Book,
  'book-open': BookOpen,
  'library': Library,
  'briefcase': Briefcase,
  'clipboard': Clipboard,
  'settings': Settings,
  'wrench': Wrench,
  'palette': Palette,
  'brush': Brush,
  'globe': Globe,
  'map': Map,
  'clock': Clock,
  'calendar': Calendar,
  'shield': Shield,
  'lock': Lock,
  'zap': Zap,
  'target': Target,
  'trophy': Trophy,
  'coffee': Coffee,
  'home': Home,
  'car': Car,
  'plane': Plane,
  'gamepad': Gamepad2,
  'shopping-bag': ShoppingBag,
  'utensils': Utensils,
  'graduation-cap': GraduationCap,
  'stethoscope': Stethoscope,
  'building': Building,
  'factory': Factory,
  'users': Users,
  'user': User,
};

interface IconPickerProps {
  selectedIcon: CategoryIcon;
  onIconChange: (icon: CategoryIcon) => void;
  className?: string;
}

export const getCategoryIcon = (iconName: CategoryIcon): LucideIcon => {
  return CATEGORY_ICONS[iconName] || CATEGORY_ICONS['folder'];
};

const IconPicker: React.FC<IconPickerProps> = ({ selectedIcon, onIconChange, className }) => {
  const iconCategories = [
    {
      title: 'Storage & Organization',
      icons: ['folder', 'folder-open', 'archive', 'package', 'clipboard', 'tag', 'tags'] as CategoryIcon[]
    },
    {
      title: 'Content Types',
      icons: ['image', 'camera', 'file-text', 'video', 'music', 'book', 'book-open', 'library'] as CategoryIcon[]
    },
    {
      title: 'Work & Business',
      icons: ['briefcase', 'building', 'factory', 'settings', 'wrench'] as CategoryIcon[]
    },
    {
      title: 'Personal & Life',
      icons: ['heart', 'star', 'bookmark', 'home', 'car', 'plane', 'coffee', 'utensils'] as CategoryIcon[]
    },
    {
      title: 'Activities & Hobbies',
      icons: ['palette', 'brush', 'gamepad', 'trophy', 'graduation-cap', 'stethoscope'] as CategoryIcon[]
    },
    {
      title: 'General',
      icons: ['globe', 'map', 'clock', 'calendar', 'shield', 'lock', 'zap', 'target', 'users', 'user', 'shopping-bag'] as CategoryIcon[]
    }
  ];

  return (
    <div className={cn("w-full", className)}>
      <div 
        className="space-y-4"
        onWheel={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {iconCategories.map((category, categoryIndex) => (
          <div key={category.title} className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {category.title}
            </h4>
            <div className="grid grid-cols-6 gap-2">
              {category.icons.map((iconKey) => {
                const IconComponent = CATEGORY_ICONS[iconKey];
                const isSelected = selectedIcon === iconKey;
                
                return (
                  <motion.button
                    key={iconKey}
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onIconChange(iconKey)}
                    className={cn(
                      "h-10 w-10 rounded-lg border-2 flex items-center justify-center transition-all duration-200",
                      isSelected
                        ? "border-purple-500 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                        : "border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:text-purple-500"
                    )}
                    title={iconKey.replace('-', ' ')}
                  >
                    <IconComponent size={18} />
                  </motion.button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IconPicker; 